import { OpacityRounded, ThermostatRounded, WbSunnyRounded } from '@mui/icons-material';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import React from 'react';
import { BASE_COLORS } from '../../../constants';
import { getDroughtStatusColors, getDroughtStatusLabel } from './droughtWarningStatusStyles';

const formatSummaryNumber = (value, maximumFractionDigits = 1) => {
    const numericValue = Number(value);

    if (value === null || typeof value === 'undefined' || Number.isNaN(numericValue)) {
        return '-';
    }

    return numericValue.toLocaleString('hu-HU', { maximumFractionDigits });
};

const StatTile = ({ icon, label, value, unit, hint, loading, sx }) => (
    <Box
        sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.14)',
            borderRadius: 1,
            minWidth: 0,
            px: 1.25,
            py: 1,
            ...sx,
        }}
    >
        <Stack direction="row" alignItems="center" gap={0.5} sx={{ opacity: 0.85 }}>
            {icon}
            <Typography sx={{ fontSize: 11, fontWeight: 700, lineHeight: 1.3 }}>{label}</Typography>
        </Stack>
        {loading ? (
            <Skeleton
                variant="text"
                width="70%"
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.3)', fontSize: 20, mt: 0.25 }}
            />
        ) : (
            <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.25, mt: 0.25 }}>
                {value}
                {unit ? (
                    <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, ml: 0.5 }}>
                        {unit}
                    </Typography>
                ) : null}
            </Typography>
        )}
        {hint && !loading ? (
            <Typography sx={{ fontSize: 11, lineHeight: 1.3, opacity: 0.85 }}>{hint}</Typography>
        ) : null}
    </Box>
);

const DroughtWarningSummary = ({
    formattedSelectedDate,
    droughtInfoRows,
    droughtInfoLoading,
    selectedTargetLabel,
    droughtDayCount,
    evaluatedDayCount,
    droughtStatusesLoading,
}) => {
    const [summaryRow] = droughtInfoRows;
    const isDrought = summaryRow?.is_drought;
    const statusColors = getDroughtStatusColors(isDrought);
    const hasData = Boolean(summaryRow);

    return (
        <Box
            sx={{
                backgroundColor: BASE_COLORS.PRIMARY_MAIN,
                borderRadius: 1,
                color: BASE_COLORS.WHITE,
                mb: 0.5,
                px: 1.5,
                py: 1.25,
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                flexWrap="wrap"
            >
                <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                    {formattedSelectedDate}
                </Typography>
                {droughtInfoLoading ? (
                    <Skeleton
                        variant="rounded"
                        width={120}
                        height={24}
                        sx={{ bgcolor: 'rgba(255, 255, 255, 0.3)' }}
                    />
                ) : (
                    <Box
                        sx={{
                            backgroundColor: statusColors.backgroundColor,
                            borderRadius: 4,
                            color: statusColors.color,
                            fontSize: 12,
                            fontWeight: 700,
                            px: 1.25,
                            py: 0.375,
                        }}
                    >
                        {hasData ? getDroughtStatusLabel(isDrought) : 'Nincs adat'}
                    </Box>
                )}
            </Stack>

            {!droughtInfoLoading && !hasData ? (
                <Typography sx={{ fontSize: 13, lineHeight: 1.45, mt: 0.75, opacity: 0.9 }}>
                    Ehhez a naphoz nem érkezett aszály adat a kiválasztott {selectedTargetLabel}.
                </Typography>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gap: 0.75,
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        mt: 1,
                    }}
                >
                    <StatTile
                        icon={<OpacityRounded sx={{ fontSize: 14 }} />}
                        label="30 NAPOS CSAPADÉK"
                        value={formatSummaryNumber(summaryRow?.precip_30d_sum_mm)}
                        unit="mm"
                        loading={droughtInfoLoading}
                    />
                    <StatTile
                        icon={<ThermostatRounded sx={{ fontSize: 14 }} />}
                        label="31 °C FELETTI NAP"
                        value={formatSummaryNumber(summaryRow?.hot_days_gt_31_count, 0)}
                        unit="nap"
                        hint="az elmúlt 30 napban"
                        loading={droughtInfoLoading}
                    />
                    <StatTile
                        sx={{ gridColumn: '1 / -1' }}
                        icon={<WbSunnyRounded sx={{ fontSize: 14 }} />}
                        label="ASZÁLYOS NAP AZ ÉVBEN"
                        value={formatSummaryNumber(droughtDayCount, 0)}
                        unit="nap"
                        hint={
                            evaluatedDayCount
                                ? `az év elejétől ${formattedSelectedDate} napig, ${formatSummaryNumber(
                                      evaluatedDayCount,
                                      0
                                  )} kiértékelt napból`
                                : 'nincs kiértékelt nap ebben az évben'
                        }
                        loading={droughtStatusesLoading}
                    />
                </Box>
            )}
        </Box>
    );
};

export default DroughtWarningSummary;
